import { useEffect, useState } from "react";
import { api, money, errorMessage } from "../api";
import { Check, Crown, Loader2, ShieldCheck, Sparkles, UtensilsCrossed, BedDouble, Gift, CreditCard } from "lucide-react";
import { fmtD } from "../lib/units";
import ProposedMatrix from "./ProposedMatrix";
import { SubscriptionPanel } from "./SubscriptionPanel";

const CYCLE_ORDER = ["monthly", "half_yearly", "yearly"];
const SAVE = { half_yearly: "Save 10% vs monthly", yearly: "Save 20% vs monthly" };
const EDITION_OPTS = [
  { id: "restaurant", label: "Restaurant Only", sub: "POS, KOT, inventory, recipes & reports", icon: UtensilsCrossed },
  { id: "hotel", label: "Restaurant + Hotel", sub: "Everything in Restaurant plus rooms, folios & halls", icon: BedDouble },
];

async function loadCheckout() {
  if (window.Razorpay) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve; s.onerror = () => reject(new Error("Could not load Razorpay Checkout")); document.body.appendChild(s);
  });
}

const pricesOf = (p, edition) => (edition === "hotel" ? p.prices_hotel || p.prices : p.prices);
const limitTxt = (v) => (v == null ? "Unlimited" : v);

function EditionPicker({ edition, setEdition }) {
  return (
    <div className="edition-picker" role="radiogroup" aria-label="Edition" data-testid="edition-picker">
      {EDITION_OPTS.map(({ id, label, sub, icon: Icon }) => (
        <button key={id} role="radio" aria-checked={edition === id} className={edition === id ? "edition-opt on" : "edition-opt"} onClick={() => setEdition(id)} data-testid={`edition-${id}`}>
          <Icon size={18} /><span><b>{label}</b><small>{sub}</small></span>
        </button>
      ))}
    </div>
  );
}

function PlanCard({ p, data, cycle, edition, mode, current, trialCurrent, busy, onPay, onTrial }) {
  const hotel = edition === "hotel";
  const feats = [...p.features, ...(hotel ? data.hotel_features?.[p.id] || [] : [])];
  const label = (f) => data.feature_catalog.find((x) => x.key === f)?.label || f;
  const lim = p.limits || {};
  return (
    <article className={`plan-card ${p.popular ? "popular" : ""} ${current || trialCurrent ? "current" : ""}`} data-testid={`plan-card-${p.id}`}>
      {p.popular && <span className="plan-ribbon"><Crown size={11} /> Most popular</span>}
      {trialCurrent && <span className="plan-ribbon trial" data-testid={`plan-trial-badge-${p.id}`}><Gift size={11} /> Your trial plan</span>}
      <header><h3>{p.name}</h3><p>{p.tagline}</p></header>
      <div className="plan-price">
        <strong data-testid={`plan-price-${p.id}`}>{money(pricesOf(p, edition)[cycle])}</strong>
        <small>{data.cycles.find((x) => x.id === cycle)?.label} · incl. 18% GST</small>
        {SAVE[cycle] && <em>{SAVE[cycle]}</em>}
      </div>
      <ul className="plan-feats">
        {feats.slice(0, 10).map((f) => <li key={f}><Check size={12} /> {label(f)}</li>)}
        {feats.length > 10 && <li className="more">+ {feats.length - 10} more</li>}
        <li className="more" data-testid={`plan-limits-${p.id}`}>Outlets <b>{lim.max_branches ?? p.max_branches}</b> · Staff <b>{limitTxt(lim.max_staff)}</b>{hotel ? <> · Rooms <b>{limitTxt(lim.max_rooms)}</b> · Halls <b>{limitTxt(lim.max_halls)}</b></> : null}</li>
      </ul>
      {mode === "trial" && (
        <button className="plan-cta" disabled={!!busy || trialCurrent} onClick={() => onTrial(p.id)} data-testid={`trial-${p.id}`}>
          {busy === `trial-${p.id}` ? <Loader2 className="spin" size={14} /> : <Gift size={14} />}{trialCurrent ? "Trial running on this plan" : `Try ${p.name} free`}
        </button>
      )}
      <button className={mode === "trial" ? "plan-cta ghost" : "plan-cta"} disabled={!!busy || current} onClick={() => onPay(p.id)} data-testid={`subscribe-${p.id}`}>
        {busy === p.id ? <Loader2 className="spin" size={14} /> : mode === "trial" ? <CreditCard size={14} /> : <Sparkles size={14} />}
        {current ? "Current plan" : mode === "paid" ? `Switch to ${p.name}` : mode === "trial" ? "Pay now & activate" : `Pay & activate ${p.name}`}
      </button>
    </article>
  );
}

function TrialBar({ ent }) {
  if (ent?.source !== "trial") return null;
  const tp = ent.trial_plan;
  const until = ent.trial_ends_at ? fmtD(ent.trial_ends_at) : "";
  return (
    <div className={`trial-bar ${ent.trial_days_remaining <= 2 ? "urgent" : ""}`} data-testid="trial-bar">
      <Gift size={16} />
      <p><b>{ent.trial_days_remaining} day(s) left in your free trial</b>{until ? ` · ends ${until}` : ""}. {tp ? <>Running on <b>{tp.plan_id[0].toUpperCase() + tp.plan_id.slice(1)} · {tp.edition === "hotel" ? "Restaurant + Hotel" : "Restaurant Only"}</b>.</> : "Pick any plan below to try it free with its full features and limits."} Nothing is charged automatically — you pay only when you choose to.</p>
    </div>
  );
}

export default function Plans({ entitlements, trialExpired, onActivated, onRefresh }) {
  const [data, setData] = useState(null);
  const [cycle, setCycleRaw] = useState("monthly");
  const [edition, setEditionRaw] = useState("restaurant");
  const [touched, setTouched] = useState(false);
  const setCycle = (c) => { setTouched(true); setCycleRaw(c); };
  const setEdition = (e) => { setTouched(true); setEditionRaw(e); };
  useEffect(() => {
    if (!entitlements || touched) return;
    setEditionRaw(entitlements.trial_plan?.edition || entitlements.edition || (entitlements.hotel_needed ? "hotel" : "restaurant"));
    setCycleRaw(entitlements.trial_plan?.cycle || entitlements.subscription?.cycle || "monthly");
  }, [entitlements, touched]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [rev, setRev] = useState(0);
  const sub = entitlements?.source === "paid" ? entitlements.subscription : null;
  const mode = entitlements?.source === "paid" ? "paid" : entitlements?.source === "trial" ? "trial" : "none";
  const tp = entitlements?.trial_plan;

  useEffect(() => { api.get("/plans").then((r) => setData(r.data)).catch(() => {}); }, []);

  const done = async (text) => { setMsg(text); setRev((x) => x + 1); await onActivated?.(); };
  const startTrial = async (planId) => {
    setBusy(`trial-${planId}`); setErr(""); setMsg("");
    try {
      await api.post("/subscribe/trial-plan", { plan_id: planId, edition, cycle });
      await onRefresh?.();
      setMsg(`Your free trial now runs on ${planId[0].toUpperCase() + planId.slice(1)} · ${edition === "hotel" ? "Restaurant + Hotel" : "Restaurant Only"}. All its features and limits are unlocked.`);
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };
  const subscribe = async (planId) => {
    setBusy(planId); setErr(""); setMsg("");
    try {
      const { data: c } = await api.post("/subscribe/checkout", { plan_id: planId, cycle, edition });
      if (c.scheduled) { setRev((x) => x + 1); setMsg(`Plan change scheduled — it starts at your next renewal.`); return; }
      if (c.starts_at) setMsg(`Your current paid period continues — the new plan's first charge will be on ${c.starts_at.slice(0, 10).split("-").reverse().join("-")}.`);
      if (c.mock) { await api.post("/subscribe/mock-pay", { subscription_id: c.subscription_id }); await done("Subscription activated (mock mode)."); return; }
      await loadCheckout();
      new window.Razorpay({
        key: c.key_id, subscription_id: c.subscription_id, name: c.app_name, description: `${c.plan_name} · ${c.edition_label} · ${c.cycle_label}`,
        prefill: c.prefill, theme: { color: "#c05a26" },
        handler: async (res) => {
          try {
            const { data: v } = await api.post("/subscribe/verify", { subscription_id: res.razorpay_subscription_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
            if (v.status === "scheduled") { setRev((x) => x + 1); setMsg(`Mandate approved — your new plan starts on ${v.starts_at.slice(0, 10).split("-").reverse().join("-")} when the current paid period ends.`); await onRefresh?.(); return; }
            await done(`Subscription active${v.invoice_no ? ` · invoice ${v.invoice_no}` : ""}.`);
          } catch (e) { setErr(errorMessage(e)); }
        },
        modal: { ondismiss: () => setBusy("") },
      }).open();
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="plans-shell" data-testid="plans-loading"><Loader2 className="spin" size={22} /> Loading plans…</div>;
  const matrixPrices = Object.fromEntries(["restaurant", "hotel"].map((ed) => [ed, data.plans.map((p) => pricesOf(p, ed)[cycle])]));
  return (
    <div className="plans-shell" data-testid="plans-view">
      {trialExpired && <div className="paywall-banner" data-testid="paywall-banner"><ShieldCheck size={16} /> {entitlements?.trial_blocked ? "This business has already used its free trial." : "Your free trial has ended."} Choose a plan and pay to continue — all your data is kept.</div>}
      {entitlements?.edition_mismatch && <div className="paywall-banner" data-testid="edition-mismatch-banner"><ShieldCheck size={16} /> The Hotel module is on in one of your branches, but your plan is Restaurant Only. Choose a Restaurant + Hotel plan to unlock rooms and halls.</div>}
      <TrialBar ent={entitlements} />
      <SubscriptionPanel key={rev} onChanged={async (t) => { setErr(""); setMsg(t); await onRefresh?.(); }} />
      <div className="plans-head">
        <span className="overline" data-testid="plans-edition-overline">PRICING · {edition === "hotel" ? "RESTAURANT + HOTEL" : "RESTAURANT ONLY"}</span>
        <h1>Simple, honest pricing for Indian restaurants.</h1>
        <p data-testid="plans-edition-note">Every plan starts with a 14-day free trial. Prices include 18% GST; you're only charged after you approve a payment.</p>
        <EditionPicker edition={edition} setEdition={setEdition} />
        <div className="cycle-toggle" role="tablist" aria-label="Billing cycle">
          {CYCLE_ORDER.map((c) => <button key={c} className={cycle === c ? "cycle on" : "cycle"} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}>{data.cycles.find((x) => x.id === c)?.label || c}</button>)}
        </div>
      </div>
      {msg && <div className="auth-alert info" data-testid="plans-message">{msg}</div>}
      {err && <div className="auth-alert error" data-testid="plans-error">{err}</div>}
      <div className="plan-grid">
        {data.plans.map((p) => <PlanCard key={p.id} p={p} data={data} cycle={cycle} edition={edition} mode={mode} busy={busy} onPay={subscribe} onTrial={startTrial}
          current={!!sub && sub.plan_id === p.id && sub.cycle === cycle && sub.edition === edition && sub.status === "active"}
          trialCurrent={mode === "trial" && !!tp && tp.plan_id === p.id && tp.edition === edition} />)}
      </div>
      <ProposedMatrix edition={edition} onEdition={setEdition} prices={matrixPrices} cycle={cycle} />
      <footer className="plans-foot"><small>Billed by {data.legal_entity} via Razorpay. Plan changes apply from your next renewal; cancelling keeps access until the paid period ends.</small></footer>
    </div>
  );
}
